import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Row, Col, Spinner, Alert, Table, Badge } from 'react-bootstrap';
import microCreditService from '../services/microCreditService';
import microCreditTransactionService from '../services/microCreditTransactionService';
import ViewPdfButton from './ViewPdfButton';

const badgeVariant = (status) => {
  switch (String(status || '').toUpperCase()) {
    case 'PENDING': return 'secondary';
    case 'CONFIRMED': return 'info';
    case 'NOT_CONFIRMED': return 'dark';
    case 'IN_PROGRESS': return 'primary';
    case 'OVERDUE': return 'warning';
    case 'CLOSED': return 'success';
    default: return 'secondary';
  }
};

export default function MicroCreditViewModal({ show, onHide, microCreditId }) {
  const [loading, setLoading] = useState(false);
  const [row, setRow] = useState(null);
  const [txs, setTxs] = useState([]);
  const [error, setError] = useState('');

  const fmtMoney = (n) => Number(n || 0).toFixed(2);
  const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : '—');

  useEffect(() => {
    if (!show || !microCreditId) return;
    setLoading(true);
    setError('');

    (async () => {
      try {
        const [account, txData] = await Promise.all([
          microCreditService.get(microCreditId),
          microCreditTransactionService.listByAccount(microCreditId),
        ]);

        setRow(account);
        setTxs(txData?.transactions || []);
      } catch (e) {
        const msg = e?.response?.data?.message || e?.message || 'Chargement impossible.';
        setError(msg);
      } finally {
        setLoading(false);
      }
    })();
  }, [show, microCreditId]);

  const typeOfTx = (t) => {
    const v = String(t || '').toUpperCase();
    if (v === 'DISBURSEMENT') return 'Décaissement';
    if (v === 'PAYMENT') return 'Paiement';
    return v || '—';
  };

  const recentTxs = useMemo(() => txs.slice(0, 5), [txs]);

  const getDocument = () => ({
    title: `Détails Micro Crédit${row?.accountNumber ? ` - #${row.accountNumber}` : ''}`,
    sections: [
      {
        title: 'Client',
        rows: [
          { label: 'Nom', value: `${row?.client?.firstName || ''} ${row?.client?.lastName || ''}`.trim() || '—' },
          { label: 'NIF/CIN', value: row?.client?.nifCin || '—' },
          { label: 'Téléphone', value: row?.client?.phone || '—' },
          { label: 'Email', value: row?.client?.email || '—' },
        ],
      },
      {
        title: 'Compte',
        rows: [
          { label: 'Numéro de compte', value: row?.accountNumber || '—' },
          { label: 'Statut', value: row?.status || '—' },
          { label: 'Devise', value: row?.currency || '—' },
          { label: 'Date création', value: fmtDate(row?.startDate) },
          { label: 'Date fin', value: fmtDate(row?.endDate) },
          { label: 'Durée (jours)', value: row?.durationDays || '—' },
          { label: 'Montant prêté', value: fmtMoney(row?.principalAmount) },
          { label: 'Frais', value: fmtMoney(row?.feeAmount) },
          { label: 'Total à encaisser', value: fmtMoney(row?.totalRepayable) },
          { label: 'Déjà encaissé', value: fmtMoney(row?.totalCollected) },
          { label: 'Reste à encaisser', value: fmtMoney(row?.remainingAmount) },
          { label: 'Décaissement fait', value: row?.disbursedAt ? 'Oui' : 'Non' },
        ],
      },
      {
        title: 'Historique statut',
        rows: [
          { label: 'Confirmé le', value: row?.confirmedAt ? new Date(row.confirmedAt).toLocaleString() : '—' },
          { label: 'Décaissé le', value: row?.disbursedAt ? new Date(row.disbursedAt).toLocaleString() : '—' },
          { label: 'Clôturé le', value: row?.closedAt ? new Date(row.closedAt).toLocaleString() : '—' },
        ],
      },
      {
        title: 'Note',
        rows: [
          { label: 'Note', value: row?.note || '—' },
        ],
      },
      {
        title: 'Dernières transactions',
        rows:
          recentTxs.length > 0
            ? recentTxs.map((tx, idx) => ({
                label: `Transaction ${idx + 1}`,
                value: `ID: ${tx.id} | Type: ${typeOfTx(tx.type)} | Montant: ${fmtMoney(tx.amount)} | Déjà encaissé après: ${fmtMoney(tx.collectedAfter)} | Reste après: ${fmtMoney(tx.remainingAfter)} | Date: ${new Date(tx.createdAt).toLocaleString()} | Code: ${tx.codeTransaction || '—'}`,
              }))
            : [{ label: 'Transactions', value: 'Aucune transaction.' }],
      },
    ],
  });

  return (
    <Modal show={show} onHide={onHide} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>
          Détails Micro Crédit {row?.accountNumber ? `#${row.accountNumber}` : ''}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {loading ? (
          <div className="text-center my-5"><Spinner animation="border" /></div>
        ) : error ? (
          <Alert variant="danger">{error}</Alert>
        ) : row ? (
          <>
            <Row className="mb-3">
              <Col md={7}>
                <h5 className="mb-3">Client</h5>
                <div><strong>Nom :</strong> {`${row.client?.firstName || ''} ${row.client?.lastName || ''}`.trim() || '—'}</div>
                <div><strong>NIF/CIN :</strong> {row.client?.nifCin || '—'}</div>
                <div><strong>Téléphone :</strong> {row.client?.phone || '—'}</div>
                <div><strong>Email :</strong> {row.client?.email || '—'}</div>
              </Col>

              <Col md={5}>
                <h5 className="mb-3">Statut</h5>
                <div>
                  <Badge bg={badgeVariant(row.status)}>{row.status}</Badge>
                </div>
                <div className="mt-2"><strong>Devise :</strong> {row.currency}</div>
                <div><strong>Date création :</strong> {fmtDate(row.startDate)}</div>
                <div><strong>Date fin :</strong> {fmtDate(row.endDate)}</div>
                <div><strong>Durée (jours) :</strong> {row.durationDays}</div>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={4}>
                <h6 className="mb-2">Montants</h6>
                <div><strong>Montant prêté :</strong> {fmtMoney(row.principalAmount)}</div>
                <div><strong>Frais :</strong> {fmtMoney(row.feeAmount)}</div>
                <div><strong>Total à encaisser :</strong> {fmtMoney(row.totalRepayable)}</div>
              </Col>

              <Col md={4}>
                <h6 className="mb-2">Paiements</h6>
                <div><strong>Déjà encaissé :</strong> {fmtMoney(row.totalCollected)}</div>
                <div><strong>Reste à encaisser :</strong> {fmtMoney(row.remainingAmount)}</div>
                <div><strong>Décaissement fait :</strong> {row.disbursedAt ? 'Oui' : 'Non'}</div>
              </Col>

              <Col md={4}>
                <h6 className="mb-2">Historique statut</h6>
                <div><strong>Confirmé le :</strong> {row.confirmedAt ? new Date(row.confirmedAt).toLocaleString() : '—'}</div>
                <div><strong>Décaissé le :</strong> {row.disbursedAt ? new Date(row.disbursedAt).toLocaleString() : '—'}</div>
                <div><strong>Clôturé le :</strong> {row.closedAt ? new Date(row.closedAt).toLocaleString() : '—'}</div>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={12}>
                <h6 className="mb-2">Note</h6>
                <div>{row.note || '—'}</div>
              </Col>
            </Row>

            <h5 className="mb-2">Dernières transactions</h5>
            <Table hover responsive size="sm" className="mb-0">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Type</th>
                  <th>Montant</th>
                  <th>Déjà encaissé après</th>
                  <th>Reste après</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {recentTxs.map(tx => (
                  <tr key={tx.id}>
                    <td>{tx.id}</td>
                    <td>{typeOfTx(tx.type)}</td>
                    <td>{fmtMoney(tx.amount)}</td>
                    <td>{fmtMoney(tx.collectedAfter)}</td>
                    <td>{fmtMoney(tx.remainingAfter)}</td>
                    <td>{new Date(tx.createdAt).toLocaleString()}</td>
                  </tr>
                ))}
                {recentTxs.length === 0 && (
                  <tr>
                    <td colSpan="6" className="text-center text-muted">Aucune transaction.</td>
                  </tr>
                )}
              </tbody>
            </Table>
          </>
        ) : (
          <Alert variant="warning">Aucune donnée.</Alert>
        )}
      </Modal.Body>

      <Modal.Footer>
        {!!row && <ViewPdfButton getDocument={getDocument} className="me-2" />}
        <Button variant="secondary" onClick={onHide}>Fermer</Button>
      </Modal.Footer>
    </Modal>
  );
}